import type { MouseEvent } from "react";
import {
  Frame,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@volca/ui/components/ui/frame";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  AddedDate,
  InvoiceCardTitleLink,
  StatusBadge,
  StFilingTargetLink,
} from "@/accounts-payable/common";
import type { ApAutomatedRunItem } from "@/accounts-payable/types";
import { AP_TAB, invoiceStatusMeta } from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";
import { AutomatedActionRow } from "./automated-action-row";

export const AutomatedInvoiceCard = ({
  item,
  onOpenInvoice,
}: {
  item: ApAutomatedRunItem;
  onOpenInvoice: () => void;
}) => {
  const actions = item.automated_agent_run.actions;
  const openInvoice = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    event.preventDefault();
    onOpenInvoice();
  };
  return (
    <Frame stacked dense className="w-full">
      {/* Invoice header, laid out as the Review tab's card: the vendor leads,
          the status badge closes the line, and the invoice number is the link
          to the invoice, so the card needs no button of its own. */}
      <FrameHeader>
        <Stack align="start" gap={0} className="w-full min-w-0">
          <Stack direction="row" gap={2} align="center" className="flex-wrap">
            <FrameTitle className="truncate">{item.vendor_name}</FrameTitle>
            <StatusBadge meta={invoiceStatusMeta(item)} />
          </Stack>
          {/* Line two: the paperwork metadata, spaced rather than dotted so a
              wrapped line leaves no dangling dot, and the amount pinned to the
              end (hidden when there is none). Sorted by created_at (arrival),
              like Review. */}
          <Stack direction="row" gap={4} align="center" className="mt-1.5">
            <Stack
              direction="row"
              gap={0}
              align="center"
              className="max-w-152 min-w-0 flex-wrap gap-x-3 gap-y-0.5 text-sm text-subtle"
            >
              <InvoiceCardTitleLink
                invoiceId={item.id}
                vendorName={item.vendor_name}
                invoiceNumber={item.invoice_number}
                fromTab={AP_TAB.automated}
                onClick={openInvoice}
              />
              <StFilingTargetLink
                document={item}
                documentType={item.document_type}
              />
              <span>
                Added <AddedDate value={item.created_at} />
              </span>
              {item.service_address && (
                <span className="truncate" title={item.service_address}>
                  {item.service_address}
                </span>
              )}
            </Stack>
            {item.total_amount_cents ? (
              <Stack
                direction="row"
                gap={4}
                align="center"
                className="flex-none"
              >
                <span className="h-5 w-px bg-line" aria-hidden />
                <span className="text-sm font-semibold text-ink tabular-nums">
                  {formatCentsOrDashIfZero(item.total_amount_cents)}
                </span>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </FrameHeader>

      {/* What the run did. Each action is a panel. Dense pulls them onto the
          frame edge; stacked, adjacent panels share a border. */}
      {actions.map((a) => (
        <FramePanel key={a.id} fit>
          <AutomatedActionRow action={a} showDivider={false} />
        </FramePanel>
      ))}
    </Frame>
  );
};
